import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import AppConsent from '../components/Modal/AppConsent';
import FooterSection from '../components/Sections/FooterSection';
import HeaderSection from '../components/Sections/HeaderSection';
import { AuthRoute, ProtectedRoute } from './routes';

/* ---------------------------------- Pages --------------------------------- */

const AccountSettingsTemplate = lazy(
    () => import('../components/Templates/AccountSettingsTemplate'),
);
const BillingHistoryTemplate = lazy(
    () => import('../components/Templates/BillingHistoryTemplate'),
);
const CollectionTemplate = lazy(
    () => import('../components/Templates/CollectionTemplate'),
);
const ConsentManagementTemplate = lazy(
    () => import('../components/Templates/ConsentManagementTemplate'),
);
const ContributorTemplate = lazy(
    () => import('../components/Templates/ContributorTemplate'),
);
const FlexPageTemplate = lazy(
    () => import('../components/Templates/FlexTemplate'),
);
const ForgotPasswordTemplate = lazy(
    () => import('../components/Templates/ForgotPasswordTemplate'),
);
const InternalLinkRedirectTemplate = lazy(
    () => import('../components/Templates/InternalLinkRedirectTemplate'),
);
const MySubscriptionsTemplate = lazy(
    () => import('../components/Templates/MySubscriptionsTemplate'),
);
const PairingTemplate = lazy(
    () => import('../components/Templates/PairingTemplate'),
);
const PaymentDetailsTemplate = lazy(
    () => import('../components/Templates/PaymentDetailsTemplate'),
);
const PaymentForSubscriptionTemplate = lazy(
    () => import('../components/Templates/PaymentForSubscriptionTemplate'),
);
const PaymentForTvTemplate = lazy(
    () =>
        import(
            '../components/Templates/PaymentTemplate/PaymentForTvTemplate.index'
        ),
);
const PaymentHandoffTemplate = lazy(
    () => import('../components/Templates/PaymentHandoffTemplate'),
);
const PaymentTemplate = lazy(
    () => import('../components/Templates/PaymentTemplate'),
);
const ProductTemplate = lazy(
    () => import('../components/Templates/ProductTemplate'),
);
const RegistrationTemplate = lazy(
    () => import('../components/Templates/RegistrationTemplate'),
);
const ResetPasswordTemplate = lazy(
    () => import('../components/Templates/ResetPasswordTemplate'),
);
const SearchResultsTemplate = lazy(
    () => import('../components/Templates/SearchResultsTemplate'),
);
const SignInTemplate = lazy(
    () => import('../components/Templates/SignInTemplate'),
);
const SubscriptionTemplate = lazy(
    () => import('../components/Templates/SubscriptionTemplate'),
);
const TvEpisodeTemplate = lazy(
    () => import('../components/Templates/TvShowTemplate/index.[episode]'),
);
const TvSeasonTemplate = lazy(
    () => import('../components/Templates/TvShowTemplate/index.[season]'),
);
const TvShowTemplate = lazy(
    () => import('../components/Templates/TvShowTemplate/index.[show]'),
);

/* --------------------------------- Routes --------------------------------- */

const FullRoutes = () => (
    <Routes>
        {/* --------------------------------- Paring --------------------------------- */}
        <Route
            path="/pair"
            element={
                <Suspense fallback={null}>
                    <PairingTemplate />
                </Suspense>
            }
        />
        <Route
            path="/pair/:pairingCode"
            element={
                <Suspense fallback={null}>
                    <PairingTemplate />
                </Suspense>
            }
        />
        <Route
            path="*"
            element={
                <HeaderSection>
                    <Routes>
                        {/* -------------------------------- Redirects ------------------------------- */}
                        <Route
                            path="/internal/:itemId"
                            element={
                                <ProtectedRoute>
                                    <Suspense fallback={null}>
                                        <InternalLinkRedirectTemplate />
                                    </Suspense>
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/auth/signin"
                            element={<Navigate to="/signin" replace />}
                        />
                        <Route
                            path="/auth/register"
                            element={<Navigate to="/register" replace />}
                        />
                        <Route
                            path="/auth/forgot"
                            element={<Navigate to="/forgot" replace />}
                        />
                        {/* ---------------------------------- Auth ---------------------------------- */}
                        <Route
                            path="*"
                            element={
                                <FooterSection
                                    render={(footer) => (
                                        <>
                                            <AppConsent />
                                            <Routes>
                                                <Route
                                                    path="/signin"
                                                    element={
                                                        <AuthRoute>
                                                            <Suspense
                                                                fallback={null}
                                                            >
                                                                <SignInTemplate />
                                                                {footer}
                                                            </Suspense>
                                                        </AuthRoute>
                                                    }
                                                />

                                                <Route
                                                    path="/register"
                                                    element={
                                                        <AuthRoute>
                                                            <Suspense
                                                                fallback={null}
                                                            >
                                                                <RegistrationTemplate />
                                                                {footer}
                                                            </Suspense>
                                                        </AuthRoute>
                                                    }
                                                />
                                                <Route
                                                    path="/verify/:token"
                                                    element={
                                                        <AuthRoute>
                                                            <Suspense
                                                                fallback={null}
                                                            >
                                                                <RegistrationTemplate />
                                                                {footer}
                                                            </Suspense>
                                                        </AuthRoute>
                                                    }
                                                />

                                                <Route
                                                    path="/forgot"
                                                    element={
                                                        <AuthRoute>
                                                            <Suspense
                                                                fallback={null}
                                                            >
                                                                <ForgotPasswordTemplate />
                                                                {footer}
                                                            </Suspense>
                                                        </AuthRoute>
                                                    }
                                                />
                                                <Route
                                                    path="/reset/:key"
                                                    element={
                                                        <AuthRoute>
                                                            <Suspense
                                                                fallback={null}
                                                            >
                                                                <ResetPasswordTemplate />
                                                                {footer}
                                                            </Suspense>
                                                        </AuthRoute>
                                                    }
                                                />
                                                {/* ----------------------------- Reference Pages ---------------------------- */}

                                                <Route
                                                    path="/collection/:reference"
                                                    element={
                                                        <ProtectedRoute>
                                                            <Suspense
                                                                fallback={null}
                                                            >
                                                                <CollectionTemplate
                                                                    footer={
                                                                        footer
                                                                    }
                                                                />
                                                            </Suspense>
                                                        </ProtectedRoute>
                                                    }
                                                />
                                                <Route
                                                    path="/contributor/:reference"
                                                    element={
                                                        <ProtectedRoute>
                                                            <Suspense
                                                                fallback={null}
                                                            >
                                                                <ContributorTemplate
                                                                    footer={
                                                                        footer
                                                                    }
                                                                />
                                                            </Suspense>
                                                        </ProtectedRoute>
                                                    }
                                                />
                                                <Route
                                                    path="/product/:reference"
                                                    element={
                                                        <ProtectedRoute>
                                                            <Suspense
                                                                fallback={null}
                                                            >
                                                                <ProductTemplate
                                                                    footer={
                                                                        footer
                                                                    }
                                                                />
                                                            </Suspense>
                                                        </ProtectedRoute>
                                                    }
                                                />
                                                <Route
                                                    path="/product/:reference/rent"
                                                    element={
                                                        <ProtectedRoute>
                                                            <Suspense
                                                                fallback={null}
                                                            >
                                                                <PaymentTemplate />
                                                                {footer}
                                                            </Suspense>
                                                        </ProtectedRoute>
                                                    }
                                                />
                                                <Route
                                                    path="/show/:reference"
                                                    element={
                                                        <ProtectedRoute>
                                                            <Suspense
                                                                fallback={null}
                                                            >
                                                                <TvShowTemplate
                                                                    footer={
                                                                        footer
                                                                    }
                                                                />
                                                            </Suspense>
                                                        </ProtectedRoute>
                                                    }
                                                />
                                                <Route
                                                    path="/season/:reference"
                                                    element={
                                                        <ProtectedRoute>
                                                            <Suspense
                                                                fallback={null}
                                                            >
                                                                <TvSeasonTemplate
                                                                    footer={
                                                                        footer
                                                                    }
                                                                />
                                                            </Suspense>
                                                        </ProtectedRoute>
                                                    }
                                                />
                                                <Route
                                                    path="/episode/:reference"
                                                    element={
                                                        <ProtectedRoute>
                                                            <Suspense
                                                                fallback={null}
                                                            >
                                                                <TvEpisodeTemplate
                                                                    footer={
                                                                        footer
                                                                    }
                                                                />
                                                            </Suspense>
                                                        </ProtectedRoute>
                                                    }
                                                />
                                                <Route
                                                    path="/:reference"
                                                    element={
                                                        <ProtectedRoute>
                                                            <Suspense
                                                                fallback={null}
                                                            >
                                                                <FlexPageTemplate
                                                                    footer={
                                                                        footer
                                                                    }
                                                                />
                                                            </Suspense>
                                                        </ProtectedRoute>
                                                    }
                                                />

                                                <Route
                                                    path="/"
                                                    element={
                                                        <ProtectedRoute>
                                                            <Suspense
                                                                fallback={null}
                                                            >
                                                                <FlexPageTemplate
                                                                    footer={
                                                                        footer
                                                                    }
                                                                />
                                                            </Suspense>
                                                        </ProtectedRoute>
                                                    }
                                                />
                                                {/* ---------------------------------- SVOD ---------------------------------- */}

                                                <Route
                                                    path="/subscribe/pay"
                                                    element={
                                                        <ProtectedRoute>
                                                            <Suspense
                                                                fallback={null}
                                                            >
                                                                <PaymentForSubscriptionTemplate />
                                                                {footer}
                                                            </Suspense>
                                                        </ProtectedRoute>
                                                    }
                                                />

                                                <Route
                                                    path="/subscriptions"
                                                    element={
                                                        <ProtectedRoute force>
                                                            <Suspense
                                                                fallback={null}
                                                            >
                                                                <MySubscriptionsTemplate />
                                                                {footer}
                                                            </Suspense>
                                                        </ProtectedRoute>
                                                    }
                                                />

                                                <Route
                                                    path="/payment-details"
                                                    element={
                                                        <ProtectedRoute force>
                                                            <Suspense
                                                                fallback={null}
                                                            >
                                                                <PaymentDetailsTemplate />
                                                                {footer}
                                                            </Suspense>
                                                        </ProtectedRoute>
                                                    }
                                                />
                                                <Route
                                                    path="/billing-history"
                                                    element={
                                                        <ProtectedRoute force>
                                                            <Suspense
                                                                fallback={null}
                                                            >
                                                                <BillingHistoryTemplate />
                                                                {footer}
                                                            </Suspense>
                                                        </ProtectedRoute>
                                                    }
                                                />

                                                {/* --------------------------------- Payment -------------------------------- */}
                                                {/* This route is specifically for TV payment */}
                                                <Route
                                                    path="/product/:id/pay"
                                                    element={
                                                        <ProtectedRoute>
                                                            <Suspense
                                                                fallback={null}
                                                            >
                                                                <PaymentForTvTemplate />
                                                                {footer}
                                                            </Suspense>
                                                        </ProtectedRoute>
                                                    }
                                                />
                                                <Route
                                                    path="/pay"
                                                    element={
                                                        <ProtectedRoute>
                                                            <Suspense
                                                                fallback={null}
                                                            >
                                                                <PaymentHandoffTemplate />
                                                                {footer}
                                                            </Suspense>
                                                        </ProtectedRoute>
                                                    }
                                                />
                                                <Route
                                                    path="/pay/:code"
                                                    element={
                                                        <ProtectedRoute>
                                                            <Suspense
                                                                fallback={null}
                                                            >
                                                                <PaymentHandoffTemplate />
                                                                {footer}
                                                            </Suspense>
                                                        </ProtectedRoute>
                                                    }
                                                />

                                                {/* ---------------------------------- Other --------------------------------- */}

                                                <Route
                                                    path="/account"
                                                    element={
                                                        <ProtectedRoute force>
                                                            <Suspense
                                                                fallback={null}
                                                            >
                                                                <AccountSettingsTemplate />
                                                                {footer}
                                                            </Suspense>
                                                        </ProtectedRoute>
                                                    }
                                                />
                                                <Route
                                                    path="/search"
                                                    element={
                                                        <ProtectedRoute>
                                                            <Suspense
                                                                fallback={null}
                                                            >
                                                                <SearchResultsTemplate />
                                                                {footer}
                                                            </Suspense>
                                                        </ProtectedRoute>
                                                    }
                                                />
                                                <Route
                                                    path="/search/:query"
                                                    element={
                                                        <ProtectedRoute>
                                                            <Suspense
                                                                fallback={null}
                                                            >
                                                                <SearchResultsTemplate />
                                                                {footer}
                                                            </Suspense>
                                                        </ProtectedRoute>
                                                    }
                                                />

                                                <Route
                                                    path="/subscribe"
                                                    element={
                                                        <ProtectedRoute>
                                                            <Suspense
                                                                fallback={null}
                                                            >
                                                                <SubscriptionTemplate />
                                                                {footer}
                                                            </Suspense>
                                                        </ProtectedRoute>
                                                    }
                                                />
                                                <Route
                                                    path="/privacy"
                                                    element={
                                                        <Suspense
                                                            fallback={null}
                                                        >
                                                            <ConsentManagementTemplate />
                                                            {footer}
                                                        </Suspense>
                                                    }
                                                />
                                            </Routes>
                                        </>
                                    )}
                                />
                            }
                        />

                        {/* -------------------------------- CatchAlls ------------------------------- */}

                        {/* @TODO: Get designs for a real 404 page - for now we will redirect to '/' */}
                        <Route path="/404" element={<Navigate to="/" />} />
                        <Route
                            path="*"
                            element={<Navigate replace to="/404" />}
                        />
                    </Routes>
                </HeaderSection>
            }
        />
    </Routes>
);

/* --------------------------------- Export --------------------------------- */

export default FullRoutes;
